import { Button, Typography } from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";

import {
  ExportCampaignMutation,
  ExportCampaignMutationVariables,
  ExportCampaignRepliesMutation,
  ExportCampaignRepliesMutationVariables,
  GetCampaignExportSignedDownloadUrlMutation,
  GetCampaignExportSignedDownloadUrlMutationVariables,
  GetCampaignRepliesExportSignedDownloadUrlMutation,
  GetCampaignRepliesExportSignedDownloadUrlMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import {
  EXPORT_CAMPAIGN,
  EXPORT_CAMPAIGN_REPLIES,
  GET_CAMPAIGN_EXPORT_SIGNED_DOWNLOAD_URL,
  GET_CAMPAIGN_REPLIES_EXPORT_SIGNED_DOWNLOAD_URL,
} from "~frontend/graphql/campaigns";
import { CampaignForView } from "~frontend/routes/subaccounts/campaignView/types";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

export type CampaignExportType = "RESULTS" | "REPLIES";

function useCampaignExportState(
  campaign: CampaignForView,
  exportType: CampaignExportType
): "idle" | "generating" | "ready" {
  const job = {
    RESULTS: campaign.campaignExportJob,
    REPLIES: campaign.campaignRepliesExportJob,
  }[exportType];

  if (!job) {
    return "idle";
  }

  if (jobInProgress(job)) {
    return "generating";
  }

  return "ready";
}

export function CampaignExportButton({
  campaign,
  buttonType,
  exportType,
  setErrorMessage,
}: {
  campaign: CampaignForView;
  buttonType?: "REFRESH_LINK" | "DEFAULT";
  exportType: "RESULTS" | "REPLIES";
  setErrorMessage?: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
}): ReactElement {
  const [downloadState, setDownloadState] = useState<
    | { mode: "idle" }
    | { mode: "loading" }
    | { mode: "loaded"; url: string }
    | { mode: "error"; error: string }
  >({ mode: "idle" });
  const exportState = useCampaignExportState(campaign, exportType);

  const formatError = useErrorMessageFormatter();
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  // CAMPAIGN RESULTS EXPORT

  const [getCampaignExportSignedDownloadUrl] = useMutation<
    GetCampaignExportSignedDownloadUrlMutation,
    GetCampaignExportSignedDownloadUrlMutationVariables
  >(GET_CAMPAIGN_EXPORT_SIGNED_DOWNLOAD_URL, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
  });

  const [exportCampaignMutation] = useMutation<
    ExportCampaignMutation,
    ExportCampaignMutationVariables
  >(EXPORT_CAMPAIGN, {
    onErrorSnackbarMessage: "Failed to start campaign export.",
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
  });

  async function downloadDataExport() {
    setDownloadState({ mode: "loading" });
    const result = await getCampaignExportSignedDownloadUrl();
    if (result.errors) {
      setDownloadState({ mode: "error", error: result.errors.join(", ") });
      throw new Error(formatError(result.errors));
    }
    setDownloadState({
      mode: "loaded",
      url: result.data!.getCampaignExportSignedDownloadUrl,
    });
  }

  // CAMPAIGN REPLIES EXPORT

  const [getCampaignRepliesExportSignedDownloadUrl] = useMutation<
    GetCampaignRepliesExportSignedDownloadUrlMutation,
    GetCampaignRepliesExportSignedDownloadUrlMutationVariables
  >(GET_CAMPAIGN_REPLIES_EXPORT_SIGNED_DOWNLOAD_URL, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
  });

  const [exportCampaignRepliesMutation] = useMutation<
    ExportCampaignRepliesMutation,
    ExportCampaignRepliesMutationVariables
  >(EXPORT_CAMPAIGN_REPLIES, {
    onErrorSnackbarMessage: "Failed to start campaign replies export.",
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
  });

  async function downloadRepliesExport() {
    setDownloadState({ mode: "loading" });
    const result = await getCampaignRepliesExportSignedDownloadUrl();
    if (result.errors) {
      setDownloadState({ mode: "error", error: result.errors.join(", ") });
      throw new Error(formatError(result.errors));
    }
    setDownloadState({
      mode: "loaded",
      url: result.data!.getCampaignRepliesExportSignedDownloadUrl,
    });
  }

  const downloadExport = {
    RESULTS: downloadDataExport,
    REPLIES: downloadRepliesExport,
  }[exportType];

  const exportMutation = {
    RESULTS: exportCampaignMutation,
    REPLIES: exportCampaignRepliesMutation,
  }[exportType];

  const label = {
    idle: "Generate Export",
    generating: "Exporting...",
    ready: "Download",
  };

  const handleClick = {
    idle: () => {
      void exportMutation();
    },
    generating: () => {},
    ready: downloadExport,
  };

  useEffect(() => {
    if (downloadState.mode === "loaded") {
      if (setErrorMessage) {
        setErrorMessage(null);
      }
      linkRef.current?.click();
      setTimeout(() => {
        setDownloadState({ mode: "idle" });
      }, 2000); // To fudge the fact that the button's state changes before the download completes.
    }
    if (downloadState.mode === "error" && setErrorMessage) {
      setErrorMessage(downloadState.error);
    }
  }, [downloadState, setErrorMessage]);

  const button: ReactElement<typeof Button> =
    buttonType === "REFRESH_LINK" ? (
      <LinkButton
        onClick={handleClick["idle"]}
        sx={{
          display: exportState === "ready" ? "auto" : "none",
          textWrap: "nowrap",
        }}
      >
        Refresh Export
      </LinkButton>
    ) : ["loading", "loaded"].includes(downloadState.mode) ? (
      <FlexBox direction="column" alignItems="flex-end">
        <Typography
          sx={{
            textTransform: "uppercase",
            fontWeight: "bold",
            color: palette.darkGray,
            textAlign: "right",
          }}
        >
          Downloading...
        </Typography>
        <Typography sx={{ textAlign: "right" }}>
          {`Your download will begin shortly. If it does not, `}
          <LinkButton
            onClick={handleClick["ready"]}
            sx={{ display: "inline", textWrap: "nowrap" }}
          >
            click here
          </LinkButton>
          .
        </Typography>
      </FlexBox>
    ) : (
      <Button
        variant="contained"
        onClick={handleClick[exportState]}
        disabled={exportState === "generating"}
      >
        {label[exportState]}
      </Button>
    );

  return (
    <>
      {button}
      {downloadState.mode === "loaded" && (
        <a ref={linkRef} href={downloadState.url} aria-disabled={true} />
      )}
    </>
  );
}
